import { FormEvent, useCallback, useEffect, useState } from 'react';
import { useForm, UseFormReturn } from 'react-hook-form';
import walletKycSchema, { WalletKyc } from '../../../schemas/wallet-kyc-schema';
import createCorporation from '../kyc/createCorporation';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store/store';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { useDialog } from '../../../context/Dialog';
import useIonicToast from '../../../hooks/useIonicToast';
import addDirector from '../kyc/addDirector';
import submitKycDocuments from '../kyc/submitKycDocument';
import createWallet from '../walletCreation/createWallet';
import { WalletSetupResume } from '../../../types/wallet/Walletkyc';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { convertUrlToFile } from '../../../utils/helperFunction/convertUrlToFile';
dayjs.extend(utc);

type UseWalletKycProps = {
  setupResume?: WalletSetupResume | null;
};

const DOCUMENT_MAP = {
  CAC: 'cac',
  TAX_IDENTIFICATION_NUMBER: 'tin',
  BOARD_RESOLUTION: 'board_resolution', // Verify backend string for this
  MEMORANDUM_AND_ARTICLES: 'memart',
  PROOF_OF_ADDRESS: 'utility_bill',
  SCUML: 'scuml',
} as any;

export function useWalletKyc(props?: UseWalletKycProps) {
  const {
    userSession: { signedAccessToken },
    userStores: { hasStore, ...userStores },
    userProfile,
  } = useSelector((state: RootState) => ({
    userStores: state.userStores,
    userSession: state.userSession,
    userProfile: state.userProfile,
  }));
  const { setBusy } = useDialog();
  const { presentIonToast } = useIonicToast();
  const [step, setStep] = useState<number>(
    props?.setupResume?.initial_step || 0
  );
  const queryClient = useQueryClient();
  const methods: UseFormReturn<WalletKyc> = useForm<WalletKyc>({
    resolver: zodResolver(walletKycSchema),
  });

  useEffect(() => {
    const loadDocuments = async () => {
      // Note: Verification check to ensure we have data
      if (!userProfile?.verification_documents?.length) return {};

      const conversionPromises = userProfile.verification_documents.map(
        async (doc: any) => {
          const schemaKey = DOCUMENT_MAP[doc.meta_info.document_name];
          // Only bother converting if we actually have a place for it in our schema
          if (schemaKey && doc.file_url) {
            const file = await convertUrlToFile(
              doc.file_url,
              doc.meta_info.document_name
            );
            return { key: schemaKey, file };
          }
          return null;
        }
      );

      const results = await Promise.all(conversionPromises);

      const formValues = results.reduce((acc: any, curr: any) => {
        if (curr && !acc[curr.key]) {
          acc[curr.key] = curr.file;
        }
        return acc;
      }, {} as any);

      // Use reset to set the default state without making the form 'dirty'
      return formValues;
    };

    const loadDefaults = async () => {
      const otherDefaultValues = {
        customer_id: props?.setupResume?.entity_id || undefined,
        business_name: userStores.activeStore?.store_name || '',
        wallet_preferred_name: userStores.activeStore?.store_name || '',
        email: userStores.activeStore?.store_email || '',
        phone: userStores.activeStore?.store_phone || '',
        business_type: 'llc',
      };
      const docs = await loadDocuments();

      methods.reset({ ...otherDefaultValues, documents: docs });
    };

    loadDefaults();
  }, [
    userProfile?.verification_documents,
    props?.setupResume,
    userStores.activeStore?.store_email,
    userStores.activeStore?.store_name,
    userStores.activeStore?.store_phone,
  ]);

  //SUBMIT STORE INFORMATION AND ADDRESS
  const submit_storeInformation = async (next?: () => void) => {
    try {
      setBusy(true);
      const { directors: _, customer_id: __, ...values } = methods.getValues();
      if (step < 1 || !signedAccessToken || !values) return;

      const { customer_id } = await createCorporation(
        {
          ...values,
          address_choice: {
            ...(values.address_choice.type === 'saved'
              ? {
                  type: values.address_choice.type,
                  saved_address_id: Number(
                    values.address_choice.saved_address_id
                  ),
                }
              : {
                  type: values.address_choice.type,
                  new_address: {
                    ...values.address_choice.new_address,
                    country: values.address_choice.new_address.country.id,
                    state: values.address_choice.new_address.state.id,
                  },
                }),
          },
          registration_info: {
            ...values.registration_info,
            incorporation_date: dayjs(
              values.registration_info.incorporation_date
            )
              .utc()
              .format('YYYY-MM-DD'),
          },
        },
        signedAccessToken
      );
      methods.setValue('customer_id', customer_id);
      presentIonToast('Store information saved!');
      next?.();
    } catch (err: any) {
      console.error(err);
      presentIonToast(
        err.message || 'Something went wrong, try again.',
        'error'
      );
    } finally {
      setBusy(false);
    }
  };

  // SUBMIT STORE DIRECTOR LIST
  const submitDirectorsList = async (next?: () => void) => {
    setBusy(true);
    const { directors, customer_id } = methods.getValues();
    if (
      !customer_id ||
      !directors ||
      directors?.length < 1 ||
      !signedAccessToken
    ) {
      setBusy(false);
      return;
    }

    try {
      const directorsQuery = directors.map((director) =>
        addDirector({
          authToken: signedAccessToken,
          corporateId: customer_id,
          director: {
            ...director,
            date_of_birth: dayjs(director.date_of_birth)
              .utc()
              .format('YYYY-MM-DD'),
          },
        })
      );

      await Promise.all(directorsQuery);
      next?.();
      presentIonToast('store directors list saved!');
    } catch (err: any) {
      console.error(err);
      presentIonToast(
        err.message || 'Something went wrong, try again.',
        'error'
      );
    } finally {
      setBusy(false);
    }
  };

  // Within your Ionic Component/Hook

  const submitDocuments = async ({
    customer_id,
    documents,
  }: Pick<WalletKyc, 'documents' | 'customer_id'>) => {
    try {
      setBusy(true);

      if (!customer_id || !signedAccessToken || !documents) {
        throw new Error('Missing required data for submission.');
      }
      const workerUrl = new URL(
        '../../../utils/web-workers/convertFileToBase64Worker.ts',
        import.meta.url
      );

      console.log(workerUrl);

      // 2. Initialize the Worker
      // The new URL syntax is the standard for Vite/Webpack 5
      const worker = new Worker(workerUrl);

      // 3. Wrap worker communication in a Promise so we can 'await' it
      const refinedDocs = await new Promise<Record<string, string>>(
        (resolve, reject) => {
          // Listen for the worker's response
          worker.onmessage = (event) => {
            const { success, data, error } = event.data;

            if (success) {
              resolve(data);
            } else {
              reject(new Error(error || 'Worker conversion failed'));
            }

            // Always terminate to free up memory on the device
            worker.terminate();
          };

          worker.onerror = (err) => {
            worker.terminate();
            reject(err);
          };

          // Send the documents object to the worker
          worker.postMessage(documents);
        }
      );

      // 4. Submit the Base64 strings to your backend
      await submitKycDocuments({
        customer_id,
        authToken: signedAccessToken,
        documents: refinedDocs, // This is now your Base64 object
      });

      // 5. Trigger Wallet Creation
      const res = await createWallet({
        customer_id,
        authToken: signedAccessToken,
      });

      if (res.success) {
        presentIonToast('Documents submitted successfully.');
        setStep(4);
      } else {
        presentIonToast(
          'Submission received, but wallet creation failed. Please contact support.',
          'error'
        );
      }
    } catch (err: any) {
      console.error('KYC Submission Error:', err);
      presentIonToast(
        err.message || 'Something went wrong, please try again.',
        'error'
      );
    } finally {
      setBusy(false);
    }
  };

  // // SUBMIT VERIFICATION DOCUMENTS
  // const submitDocuments = async ({ customer_id, documents }: WalletKyc) => {
  //   try {
  //     setBusy(true);
  //     if (!customer_id || !signedAccessToken || !documents) return;

  //     const docKeys = Object.keys(documents) as Array<keyof typeof documents>;
  //     let refinedDocs = {} as Record<keyof typeof documents, string>;

  //     docKeys.forEach(async (key) => {
  //       try {
  //         const base64 = await convertFileToBase64(documents[key]);
  //         refinedDocs[key] = base64 as string;
  //       } catch (error) {
  //         throw error;
  //       }
  //     });

  //     await submitKycDocuments({
  //       customer_id,
  //       authToken: signedAccessToken,
  //       documents: refinedDocs
  //     });

  //     const res = await createWallet({
  //       customer_id,
  //       authToken: signedAccessToken,
  //     });

  //     if (res.success) presentIonToast('documents submitted successfully.');
  //     else {
  //       presentIonToast(
  //         'Your request was received, but wallet creation failed.Please try again later.',
  //         'error'
  //       );
  //     }

  //     setStep(4);
  //   } catch (err: any) {
  //     console.error(err);
  //     presentIonToast(
  //       err.message || 'Something went wrong, try again.',
  //       'error'
  //     );
  //   } finally {
  //     setBusy(false);
  //   }
  // };

  const next = useCallback(async () => {
    // determine fields to validate based on current step
    let fields: Array<string> = [];
    if (step === 0) {
      fields = [
        'business_name',
        'business_registration_number',
        'business_type',
        'email',
        'phone',
        'registration_info',
        'tin',
        'registration_info',
      ];
    } else if (step === 1) {
      fields = ['address_choice'];
    } else if (step === 2) {
      fields = ['directors'];
    } else if (step === 3) {
      fields = [
        'documents.cac',
        'documents.tin',
        'documents.board_resolution',
        'documents.memart',
        'documents.utility_bill',
        'documents.scuml',
      ];
    }

    // trigger validation for selected fields
    const valid = await methods.trigger(fields as any);
    const next = () => setStep((s) => Math.min(s + 1, 4));

    if (valid) {
      //LOGIC FOR SUBMITTING STORE INFORMATION
      if (step == 1) await submit_storeInformation(next);
      else if (step == 2) await submitDirectorsList(next);
      else next();
    }
  }, [methods, step]);

  const handleFormSubmission = async (event: FormEvent) => {
    event.preventDefault();
    const isValid = await methods.trigger(['documents']);
    if (!isValid) return;

    const { documents, customer_id } = methods.getValues();
    await submitDocuments({ documents, customer_id });
    queryClient.invalidateQueries({ queryKey: ['wallet'] });
    queryClient.invalidateQueries({ queryKey: ['wallet-status'] });
  };

  const back = useCallback(() => setStep((s) => Math.max(0, s - 1)), []);
  const goTo = useCallback((n: number) => setStep(n), []);

  return {
    methods,
    step,
    next,
    back,
    goTo,
    handleFormSubmission,
    setStep,
    hasStore,
  };
}
